import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { ResponsiveContainer, LineChart, XAxis, YAxis, Line, CartesianGrid, Tooltip } from 'recharts';
import './MonitoringLineChart.less';


const colors = {
	line0: '#1E88E5',
	line1: '#F7CC00',
	line2: '#04E762',
	line3: '#C287E8',
	line4: '#FF7043',
	line5: '#E1CA96',
	line6: '#FF3C38',
	line7: '#06b2bc',
	line8: '#F7567C',
	line9: '#791E94',
	line10: '#FB8C00',
	line11: '#B0CB1D',
	line12: '#DC0073',
	line13: '#39EFF9',
	line14: '#FFE45C',
	line15: '#5EFDA0',
	line16: '#E5CCF5',
	line17: '#FFA185',
	line18: '#F2E7CF',
	line19: '#FF8785',
	line20: '#7FBBF0',
	line21: '#F98AA4',
	line22: '#C467E0',
	line23: '#FFBF70',
	line24: '#D8EB70',
	line25: '#FF47A6',
};


const CustomAxisTick = (props) => {
  const { x, y, payload, dateRange } = props;

  const dateDifferenceInDays = moment(dateRange.endTime).diff(moment(dateRange.startTime), 'days');
  let dateFormat = '';

  if (dateDifferenceInDays < 2) {
    dateFormat = 'MMM D (h:mm a)';
  } else if (dateDifferenceInDays > 2 && dateDifferenceInDays < 30) {
    dateFormat = 'MMM D (h:mm a)';
  } else {
    dateFormat = 'MMM D (h:mm a)';
  }

  const formattedDate = moment(payload.value).format(dateFormat);

  return (
    <g transform={`translate(${x},${y})`}>
      <text x={0} y={15} dy={10} dx={-50} fill="#475867">
        {formattedDate}
      </text>
    </g>
  );
};

function restructureData(formattedDataPoints) {
	const keys = Object.keys(formattedDataPoints);
	const dataArray = [];
	let longest = 0;
	let longestData = [];

	// create initial object with all keys set to null
	const initializeDataPoints = keys.reduce((o, key) => {
		return {
			...o,
			[key]: null,
			[`${key}__gap`]: null,
		};
	}, {});

	// find the data point with the most data points
	keys.forEach((reg) => {
		if (formattedDataPoints[reg].length > longest) {
			longestData = formattedDataPoints[reg];
			longest = formattedDataPoints[reg].length;
		}
	});

	// add initialized registers with null data, longest data set and dates to dataArray
	longestData.forEach((dataset) => {
		dataArray.push({
			...initializeDataPoints,
			...dataset,
		});
	});

	// go through the registers and replace null values if applicable
	keys.forEach((register) => {
		const values = formattedDataPoints[register];

		values.forEach((dataset, index) => {
			if (formattedDataPoints[register].length === longest) {
				// If the data length is the same as the longest data length
				// ie. there is no missing data
				dataArray[index][register] = dataset[register];
			} else {
				const indexOfDate = dataArray.findIndex((element) => {
					const { _i: elDateFormat } = element.date;
					const { _i: datasetDateFormat } = dataset.date;
					return elDateFormat === datasetDateFormat;
				});
				dataArray[indexOfDate][register] = dataset[register];
			}
		});
	});

	/* eslint-disable no-param-reassign */
	keys.forEach((register) => {
		const gapKey = `${register}__gap`;

		const firstValidRow = dataArray.find((row) => row[register] != null);
		let lastValue = firstValidRow ? firstValidRow[register] : null;

		dataArray.forEach((row) => {
			const value = row[register];

			if (value != null) {
				lastValue = value;
				row[gapKey] = value;
			} else {
				row[gapKey] = lastValue;
			}
		});
	});

	return dataArray;
}

export const MonitoringLineChart = (props) => {
	const { graphData, dateRange, selectedCategory, theme, colorObj } = props;
	const [formattedData, setFormattedData] = useState({});
	const [dataPoints, setDataPoints] = useState([]);
	const [dataPointColors, setDataPointColors] = useState([]);

	const assignColors = (formattedTags) => {
		const selectedColors = {};
		formattedTags.forEach((dataPoint) => {
			selectedColors[dataPoint] = colors[colorObj[dataPoint]];
		});
		setDataPointColors(selectedColors);
	};

	useEffect(() => {
		const formattedDataPoints = {};
		const formattedTags = [];

		if (graphData.data_points) {

			graphData.data_points.map((dataSet) => {
				return formattedTags.push(dataSet.id);
			});

			graphData.data_points.map((dataSet) => {
				formattedDataPoints[dataSet.id] = dataSet.graph_data.map((d) => ({
					date: d.x,
					[dataSet.id]: d.actual,
					// metadata: { 
					// connection_fault_count: d.connection_fault_count,
					// connection_fault: d.connection_fault
					// }
				}));

				return formattedDataPoints;
			});
		}

		setDataPoints(formattedTags);
		setFormattedData(restructureData(formattedDataPoints));
		assignColors(formattedTags);
	}, [graphData, selectedCategory, colorObj]);

	const formatTime = () => {
		const dateDifferenceInDays = moment(dateRange.endTime).diff(moment(dateRange.startTime), 'days');
		let dateFormat = '';

		if (dateDifferenceInDays < 2) {
			dateFormat = 'MMM D, YYYY (LT)';
		} else if (dateDifferenceInDays > 2 && dateDifferenceInDays < 30) {
			dateFormat = 'MMM D, YYYY (LT)';
		} else {
			dateFormat = 'MMM D, YYYY (LT)';
		}

		return dateFormat;
	};

	const tooltipFormatter = (value, name) => {
    let metric = '';
    let displayName = '';

    if (graphData.data_points) {
      graphData.data_points.forEach((datapoint) => {
        if (datapoint.id === name) {
          metric = datapoint.unit;
          displayName = datapoint.name;
        }
      });
    }

    return (
      <div>
        {displayName}
        : 
        <span className='tt-value'>
          {value !== null ? value.toFixed(2) : <span className='tt-offline'>DISCONNECTED</span>}
          <span className='tt-unit'>
            {value ? metric : ''}
          </span>
        </span>
      </div>)
  };

	const CustomTooltip = ({ active, label, payload }) => {
    if (!active || !payload?.length) return null;

    return (
      <div className={`tooltip tooltip-${theme}`}>
        <div className="tooltip-date">{moment(label).format(formatTime())}</div>

        {payload.sort((a, b) => a.dataKey.localeCompare(b.dataKey)).map((item) => (
          item.dataKey.includes('__gap') ?
          null
          :
          <div key={item.dataKey} className="tt-datapoint">
            <span
              style={{
                background: item.color ?? item.stroke ?? item.payload?.stroke,
              }}
              className="tt-circle"
            />
            <div>{tooltipFormatter(item.value, item.dataKey)}</div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <ResponsiveContainer>
      <LineChart data={formattedData}>
        <CartesianGrid strokeDasharray="3 3" vertical={false} />
        <XAxis type="category" height={80} dataKey="date" tick={<CustomAxisTick dateRange={dateRange} />} />
        <YAxis type="number" />
        {dataPoints.map((dataPoint) => (
          <React.Fragment key={dataPoint}>
            <Line
              dataKey={dataPoint}
              stroke={dataPointColors[dataPoint]}
              dot={({ cx, cy, index }) => {
                const current = formattedData[index]?.[dataPoint];
                const previous = formattedData[index - 1]?.[dataPoint];
                const next = formattedData[index + 1]?.[dataPoint];

                const isIsolatedPoint =
                  current != null &&
                  previous == null &&
                  next == null;

                if (!isIsolatedPoint) return null;

                return (
                  <circle
                    cx={cx}
                    cy={cy}
                    r={2.5}
                    fill={dataPointColors[dataPoint]}
                    stroke={dataPointColors[dataPoint]}
                  />
                );
              }}
              strokeWidth={2}
              connectNulls={false}
            />

            <Line
              dataKey={`${dataPoint}__gap`}
              stroke={`${dataPointColors[dataPoint]}66`}
              strokeWidth={1.5}
              strokeDasharray="5 5"
              connectNulls={false}
              legendType="none"
              dot={false}
            />
          </React.Fragment>
      ))}
        <Tooltip
          content={<CustomTooltip />}
          isAnimationActive={false}
          filterNull={false}
        />
      </LineChart>
    </ResponsiveContainer>
  );
};

MonitoringLineChart.propTypes = {
	graphData: PropTypes.shape({
		date: PropTypes.string,
		interval: PropTypes.string,
		name: PropTypes.string,
		slug: PropTypes.string,
		data_points: PropTypes.arrayOf(
			PropTypes.shape({
				graph_data: PropTypes.arrayOf(
					PropTypes.shape({
						actual: PropTypes.number,
						x: PropTypes.string,
					}),
				),
				id: PropTypes.string,
				name: PropTypes.string,
				tag: PropTypes.string,
				unit: PropTypes.string,
			}),
		),
	}).isRequired,
};
